import {
  applySnapshot,
  flow,
  Instance,
  SnapshotIn,
  types,
} from "mobx-state-tree";
import {
  createDeliveryStatsModel,
  DeliveryStatsModel,
} from "../models/DeliveryStatsModel";
import { GetDeliveryStatsForProjectRPC } from "@pulse/pulse-rpcs/lib/email-cycle/getDeliveryStatsForProjectRPC";
import { getAPIClient } from "../../networking/APIClient";
import { LeoUUID } from "@surya-digital/leo-ts-runtime";
import { LeoRPCResult } from "@surya-digital/leo-ts-types";
import { useGetDeliveryStatsForProjectRPCClientImpl } from "../rpcs/RPC";

const getInitialState = (): SnapshotIn<
  typeof DeliveryStatsForProjectStore
> => ({
  deliveryStats: createDeliveryStatsModel(),
  loadingDeliveryStats: false,
  getDeliveryStatsForProjectRPCError: null,
});

export const DeliveryStatsForProjectStore = types
  .model("DeliveryStatsForProjectStore", {
    deliveryStats: DeliveryStatsModel,
    loadingDeliveryStats: types.boolean,
    getDeliveryStatsForProjectRPCError: types.maybeNull(
      types.enumeration(
        "GetDeliveryStatsRPCError",
        Object.values(GetDeliveryStatsForProjectRPC.RPCError),
      ),
    ),
  })
  .actions((store) => ({
    getDeliveryStatsForProject: flow(function* (projectId: string) {
      store.loadingDeliveryStats = true;
      store.getDeliveryStatsForProjectRPCError = null;

      try {
        const apiClient = getAPIClient(store);
        const request = new GetDeliveryStatsForProjectRPC.Request(
          new LeoUUID(projectId),
        );

        const result: LeoRPCResult<
          GetDeliveryStatsForProjectRPC.Response,
          GetDeliveryStatsForProjectRPC.Errors.Errors
        > =
          yield useGetDeliveryStatsForProjectRPCClientImpl(apiClient).execute(
            request,
          );

        if ("response" in result) {
          store.deliveryStats.setStats(result.response.deliveryStats);
          return;
        }

        switch (result.error.code) {
          case GetDeliveryStatsForProjectRPC.RPCError.InvalidProjectId:
            store.getDeliveryStatsForProjectRPCError = result.error.code;
            break;
          default:
            result.error.code satisfies never;
        }
      } finally {
        store.loadingDeliveryStats = false;
      }
    }),
    resetStore: (): void => applySnapshot(store, getInitialState()),
  }));

export const createDeliveryStatsForProjectStore = (): Instance<
  typeof DeliveryStatsForProjectStore
> => DeliveryStatsForProjectStore.create(getInitialState());
